/**
 * One-time utility to fix properties with null values
 * Run this to clean up existing broken properties in IndexedDB
 */

import { db } from '../db/database';
import { syncEngine } from '../sync/SyncEngine';
import { supabase } from '../lib/supabase';
import { restDelete, restSelect, getUserId } from '../lib/supabaseRest';

export async function fixNullPropertyValues(): Promise<{
  fixed: number;
  errors: string[];
}> {
  console.log('[FixNullValues] 🔧 Starting property value fix...');

  const errors: string[] = [];
  let fixed = 0;

  try {
    // Get all properties
    const allProperties = await db.properties.toArray();
    console.log(`[FixNullValues] Found ${allProperties.length} properties to check`);

    for (const property of allProperties) {
      if (property.value === null || property.value === undefined) {
        console.log(`[FixNullValues] Fixing property ${property.id} (${property.name}) with null value`);

        try {
          // Determine appropriate default value based on type
          let defaultValue: any = '';

          if (property.type === 'relation' || property.type === 'rollup' || property.type === 'formula') {
            defaultValue = [];
          } else if (property.type === 'checkbox') {
            defaultValue = false;
          } else if (property.type === 'number') {
            defaultValue = 0;
          } else if (property.type === 'multi_select') {
            defaultValue = [];
          }

          // Update in IndexedDB
          await db.properties.update(property.id, {
            value: defaultValue,
            updatedAt: new Date().toISOString()
          });

          // Re-queue for sync with correct value AND all required fields
          // IMPORTANT: Must include ALL fields, not just value!
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            await syncEngine.queueChange('properties', 'update', property.id, {
              object_id: property.objectId,
              name: property.name,
              type: property.type,
              value: defaultValue, // ✅ Fixed value
              user_id: user.id,
              updated_at: new Date().toISOString()
            });
          }

          fixed++;
        } catch (error) {
          const errorMsg = `Failed to fix property ${property.id}: ${error}`;
          console.error(`[FixNullValues] ${errorMsg}`);
          errors.push(errorMsg);
        }
      }
    }

    console.log(`[FixNullValues] ✅ Fixed ${fixed} properties with ${errors.length} errors`);

    return { fixed, errors };
  } catch (error) {
    console.error('[FixNullValues] ❌ Fatal error:', error);
    throw error;
  }
}

/**
 * Clear failed sync entries for properties from Supabase sync_queue
 * This is critical - old entries in sync_queue have stale null values!
 */
export async function clearFailedPropertySync(): Promise<{ local: number; remote: number }> {
  console.log('[FixNullValues] 🗑️ Clearing failed property sync entries...');

  let localCleared = 0;
  let remoteCleared = 0;

  try {
    // 1. Clear from local IndexedDB sync_queue (if it exists)
    try {
      const syncQueue = await db.table('sync_queue').toArray();

      for (const entry of syncQueue) {
        if (entry.table_name === 'properties' && entry.status === 'error') {
          await db.table('sync_queue').delete(entry.id);
          localCleared++;
        }
      }
      console.log(`[FixNullValues] ✅ Cleared ${localCleared} local sync entries`);
    } catch (error) {
      console.warn('[FixNullValues] Local sync_queue not found (ok):', error);
    }

    // 2. Clear from Supabase sync_queue (the main culprit!)
    // Get user ID using the same method as SyncEngine
    const userId = getUserId();
    if (!userId) {
      console.warn('[FixNullValues] No user ID found, skipping remote sync_queue cleanup');
      console.warn('[FixNullValues] Make sure you are logged in before running this utility');
      return { local: localCleared, remote: 0 };
    }

    console.log('[FixNullValues] 🌐 Clearing remote sync_queue entries for properties...');
    console.log('[FixNullValues] User ID:', userId);

    // First, let's see what we're about to delete
    const { data: entriesToDelete, error: selectError } = await restSelect('sync_queue', {
      select: 'id,record_id,retry_count,last_error',
      eq: {
        user_id: userId,
        table_name: 'properties',
        synced: false
      }
    });

    if (selectError) {
      console.error('[FixNullValues] ❌ Error querying sync_queue:', selectError);
      return { local: localCleared, remote: 0 };
    }

    const failedEntries = (entriesToDelete || []).filter(e => (e.retry_count || 0) >= 1);
    console.log(`[FixNullValues] Found ${failedEntries.length} failed property sync entries to clear:`);
    failedEntries.forEach(e => {
      console.log(`  - ${e.record_id} (retry: ${e.retry_count}, error: ${e.last_error?.substring(0, 50)}...)`);
    });

    // Delete failed property entries from Supabase sync_queue
    // These are the entries causing the infinite retry loop!
    if (failedEntries.length > 0) {
      for (const entry of failedEntries) {
        const { error: deleteError } = await restDelete('sync_queue', {
          eq: { id: entry.id }
        });

        if (deleteError) {
          console.error(`[FixNullValues] ❌ Error deleting entry ${entry.id}:`, deleteError);
        } else {
          remoteCleared++;
          console.log(`[FixNullValues] ✅ Deleted entry ${entry.record_id}`);
        }
      }
    } else {
      console.log('[FixNullValues] No failed entries to clear');
    }

    return { local: localCleared, remote: remoteCleared };
  } catch (error) {
    console.error('[FixNullValues] ❌ Error clearing sync queue:', error);
    return { local: localCleared, remote: remoteCleared };
  }
}
